import { dolomiteEmode, dolomiteIsolation } from '@1delta/data-sdk'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { computeUtilization } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { dolomiteSubgraphUrl } from '../../dolomite-subgraph'
import { buildDolomiteConfigs } from './emodeConfig'
import { resolveDolomiteRowIdentity } from './isolation'
import { lenderShortName } from '../../../utils/marketName'

const LENDER = 'DOLOMITE'
const SUBGRAPH_TIMEOUT_MS = 12_000

interface RawRiskInfo {
  marginPremium: string
  isBorrowingDisabled: boolean
  token: {
    id: string
    symbol: string
    decimals: string
    marketId: string
    supplyLiquidity: string
    borrowLiquidity: string
  }
}
interface RawRate {
  token: { id: string }
  supplyInterestRate: string
  borrowInterestRate: string
}

export interface DolomiteSubgraphRaw {
  /** global `liquidationRatio` = 1 + marginRatio (R) */
  liquidationRatio: number
  riskInfos: RawRiskInfo[]
  /** token id → { supply, borrow } APR fractions */
  rates: { [tokenId: string]: { supply: number; borrow: number } }
}

/**
 * One GraphQL query returns the whole market set: global margin ratio, per-market
 * risk params + liquidity, and supply/borrow APRs. Replaces the ~2 + 7·N on-chain
 * calls of the on-chain path. Returns `null` on any failure so the caller falls
 * back to on-chain.
 */
export async function fetchDolomiteMarketsFromSubgraph(
  chainId: string,
): Promise<DolomiteSubgraphRaw | null> {
  const url = dolomiteSubgraphUrl(chainId)
  if (!url) return null

  const query = `{
    dolomiteMargins(first: 1) { liquidationRatio }
    marketRiskInfos(first: 1000) {
      marginPremium
      isBorrowingDisabled
      token { id symbol decimals marketId supplyLiquidity borrowLiquidity }
    }
    interestRates(first: 1000) {
      token { id }
      supplyInterestRate
      borrowInterestRate
    }
  }`

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), SUBGRAPH_TIMEOUT_MS)
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
      signal: controller.signal,
    })
    if (!response.ok) return null
    const json = (await response.json()) as {
      data?: {
        dolomiteMargins?: { liquidationRatio: string }[]
        marketRiskInfos?: RawRiskInfo[]
        interestRates?: RawRate[]
      }
    }
    const d = json?.data
    if (!d?.marketRiskInfos?.length) return null

    const rates: DolomiteSubgraphRaw['rates'] = {}
    for (const r of d.interestRates ?? []) {
      rates[r.token.id.toLowerCase()] = {
        supply: Number(r.supplyInterestRate),
        borrow: Number(r.borrowInterestRate),
      }
    }
    return {
      liquidationRatio: Number(d.dolomiteMargins?.[0]?.liquidationRatio ?? 0),
      riskInfos: d.marketRiskInfos,
      rates,
    }
  } catch {
    return null
  } finally {
    clearTimeout(timeout)
  }
}

/**
 * Convert raw subgraph markets into the same `{ data, chainId }` shape the
 * on-chain converter emits — one entry per asset under the single `DOLOMITE`
 * key. USD prices come from the `prices` map (populated by the on-chain oracle
 * fetcher) because the subgraph's `derivedETH` is unreliable for non-AMM tokens.
 *
 * Collateral factors are derived from R (= liquidationRatio − 1) and the
 * per-market margin premium p — identical to the on-chain parser (§3).
 */
export function convertDolomiteSubgraphMarkets(
  raw: DolomiteSubgraphRaw,
  chainId: string,
  prices: { [a: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): { data: Record<string, any>; chainId: string } {
  const R = raw.liquidationRatio > 0 ? raw.liquidationRatio - 1 : 0
  const emode = dolomiteEmode()?.[chainId]
  const isolation = dolomiteIsolation()?.[chainId]
  const result: Record<string, any> = {}

  for (const info of raw.riskInfos) {
    const underlying = info.token.id.toLowerCase()
    const meta = tokenList[underlying]

    const oracleKey =
      toOracleKey(meta?.assetGroup!) ?? toGenericPriceKey(underlying, chainId)
    const price = prices[underlying] ?? prices[oracleKey] ?? 0

    const totalDeposits = info.token.supplyLiquidity ?? '0'
    const totalDebt = info.token.borrowLiquidity ?? '0'
    const liquidity = Number(totalDeposits) - Number(totalDebt)

    const rate = raw.rates[underlying] ?? { supply: 0, borrow: 0 }
    const depositRate = rate.supply * 100
    const variableBorrowRate = rate.borrow * 100

    const p = Number(info.marginPremium) || 0
    const isClosing = Boolean(info.isBorrowingDisabled)
    const config = buildDolomiteConfigs(
      info.token.marketId,
      p,
      R,
      isClosing,
      emode,
      isolation,
    )

    // Key by Dolomite's marketId (protocol-native, collision-proof), not the
    // token address — must match publicCallParse + userCallParse + oracle fetcher.
    const marketUid = createMarketUid(
      chainId,
      LENDER,
      String(info.token.marketId),
    )
    const identity = resolveDolomiteRowIdentity(
      chainId,
      LENDER,
      info.token.marketId,
      underlying,
      tokenList,
    )
    result[marketUid] = {
      marketUid,
      name: meta
        ? identity.name
        : lenderShortName(LENDER) + ' ' + (info.token.symbol ?? ''),
      poolId: underlying,
      underlying,
      asset: identity.asset,
      marketId: info.token.marketId,
      totalDeposits,
      totalDebt,
      totalDepositsUSD: price * Number(totalDeposits),
      totalDebtUSD: price * Number(totalDebt),
      totalLiquidity: liquidity,
      borrowLiquidity: liquidity,
      totalLiquidityUSD: liquidity * price,
      borrowLiquidityUSD: liquidity * price,
      utilization: computeUtilization(totalDeposits, totalDebt),
      price,
      depositRate,
      variableBorrowRate,
      stableBorrowRate: 0,
      collateralActive: true,
      // Keyed by ASSET GROUP like every other lender (the map is
      // `intrinsicYields[assetGroup]`, never by address) — and for an
      // isolation market by the UNDERLYING's group, since a dGM earns what
      // its GM earns.
      intrinsicYield:
        additionalYields?.intrinsicYields?.[identity.yieldKey] ?? 0,
      rewards: [],
      config,
      closeFactor: 1,
      borrowingEnabled: !isClosing,
      // Deposits are NOT gated by this flag — see the note in `publicCallParse`.
      // The subgraph names it `isBorrowingDisabled`, which says so outright.
      depositsEnabled: true,
      ...(identity.isolation && {
        isolation: identity.isolation,
        allowedDebtMarketUids: identity.isolation.allowedDebtMarketUids,
      }),
    }
  }

  return { data: result, chainId }
}

/** Convenience: fetch + convert. Returns `null` on subgraph failure → on-chain fallback. */
export async function fetchDolomitePublicDataFromSubgraph(
  chainId: string,
  prices: { [a: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): Promise<{ data: Record<string, any>; chainId: string } | null> {
  const raw = await fetchDolomiteMarketsFromSubgraph(chainId)
  if (!raw) return null
  return convertDolomiteSubgraphMarkets(
    raw,
    chainId,
    prices,
    additionalYields,
    tokenList,
  )
}
